import { useParams, Link } from 'react-router-dom';
import { useProduct } from '../hooks/useProducts';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../hooks/useAuth';
import { Button } from '@components/ui/Button';
import { Badge } from '@components/ui/Badge';
import { DemoEmbed } from '@components/demo/DemoEmbed';
import { ReviewList } from '@components/review/ReviewList';
import { ReviewForm } from '@components/review/ReviewForm';
import { formatCurrency } from '../lib/formatCurrency';

export function ProductPage() {
  const { slug } = useParams<{ slug: string }>();
  const { product, isLoading, error } = useProduct(slug || '');
  const { addItem } = useCart();
  const { user } = useAuth();

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-1/3 rounded bg-navy-800" />
        <div className="aspect-video rounded-xl bg-navy-800" />
        <div className="h-32 rounded-xl bg-navy-800" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="py-12 text-center">
        <h2 className="text-xl font-bold text-white">Product not found</h2>
        <Link to="/" className="mt-4 inline-block text-green-500 hover:text-green-400">Back to home</Link>
      </div>
    );
  }

  function handleAddToCart() {
    if (!product) return;
    addItem({
      productId: product.id,
      name: product.name,
      slug: product.slug,
      price: product.priceOneTime || 0,
      thumbnailUrl: product.thumbnailUrl,
    });
  }

  return (
    <div className="space-y-8">
      <div>
        <Link to={product.category ? `/category/${product.category.slug}` : '/'} className="text-sm text-gray-400 hover:text-white transition-colors">
          &larr; {product.category?.name || 'All Products'}
        </Link>
      </div>

      <div className="grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {/* Demo */}
          <DemoEmbed demoType={product.demoType} demoUrl={product.demoUrl} />

          {/* Screenshots */}
          {product.screenshots.length === 3 ? (
            <div className="grid gap-4 sm:grid-cols-2 sm:grid-rows-2">
              <img src={product.screenshots[0]} alt={`${product.name} screenshot 1`} className="rounded-lg border border-white/10 w-full h-full object-cover" />
              <img src={product.screenshots[1]} alt={`${product.name} screenshot 2`} className="rounded-lg border border-white/10 w-full h-full object-cover sm:row-span-2" />
              <img src={product.screenshots[2]} alt={`${product.name} screenshot 3`} className="rounded-lg border border-white/10 w-full h-full object-cover" />
            </div>
          ) : product.screenshots.length > 0 ? (
            <div className={`grid gap-4 ${product.screenshots.length > 1 ? 'sm:grid-cols-2' : ''}`}>
              {product.screenshots.map((url, i) => (
                <img key={i} src={url} alt={`${product.name} screenshot ${i + 1}`} className="rounded-lg border border-white/10" />
              ))}
            </div>
          ) : null}

          {/* Description */}
          <div className="rounded-xl border border-white/10 bg-navy-900/50 p-6">
            <h2 className="text-lg font-semibold text-white mb-3">About</h2>
            <p className="text-gray-400 whitespace-pre-line">{product.description}</p>
          </div>

          {/* Reviews */}
          <div className="rounded-xl border border-white/10 bg-navy-900/50 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Reviews</h2>
            {product.reviews && <ReviewList reviews={product.reviews} />}
            {user && slug && (
              <div className="mt-6 border-t border-white/10 pt-6">
                <h3 className="text-md font-semibold text-white mb-3">Write a Review</h3>
                <ReviewForm productSlug={slug} />
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="sticky top-20 rounded-xl border border-white/10 bg-navy-900/50 p-6 space-y-4">
            <h1 className="text-2xl font-bold text-white">{product.name}</h1>
            <div className="flex items-center gap-2">
              <Badge variant="info">{product.priceType.replace('_', ' ')}</Badge>
              {product.isFeatured && <Badge variant="success">Featured</Badge>}
              {product.isComingSoon && <Badge variant="warning">Coming Soon</Badge>}
            </div>

            {!product.isComingSoon && (
              <>
                {product.priceOneTime && (
                  <p className="text-3xl font-bold text-gradient-green">{formatCurrency(product.priceOneTime)}</p>
                )}

                {product.priceMonthly && (
                  <p className="text-sm text-gray-400">
                    or {formatCurrency(product.priceMonthly)}/mo
                  </p>
                )}

                <Button
                  onClick={handleAddToCart}
                  size="lg"
                  className="w-full bg-gradient-to-r from-teal-500 to-green-500 hover:shadow-[0_0_20px_rgba(8,139,126,0.3)]"
                  data-testid="add-to-cart"
                  data-product-id={product.id}
                >
                  Add to Cart
                </Button>

                {/* Feature bullets — rendered when product has features field */}
                {'features' in product && Array.isArray(product.features) && (
                  <ul className="space-y-2 pt-2">
                    {(product.features as string[]).map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-gray-300">
                        <svg className="h-4 w-4 flex-shrink-0 text-teal-400" fill="currentColor" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" /></svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                )}

                {/* Try Free Demo */}
                {product.demoUrl && (
                  <a
                    href={product.demoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 block w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-center text-sm font-semibold text-white backdrop-blur transition-all hover:bg-white/10"
                  >
                    Try Free Demo
                  </a>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
